// src/pages/Legal/RiskDisclosure.tsx

import React from 'react';
import styles from './Legal.module.css';

const TOC = [
  { id: 'trading-risk', label: 'Trading Risk' },
  { id: 'no-guarantees', label: 'No Guarantees' },
  { id: 'not-advice', label: 'Not Financial Advice' },
  { id: 'automated-risk', label: 'Automated Trading Risks' },
  { id: 'options-risk', label: 'Options Risk' },
  { id: 'market-data', label: 'Market Data' },
  { id: 'regulatory', label: 'Regulatory Compliance' },
];

const RiskDisclosure: React.FC = () => {
  return (
    <div className={styles.legalPage} id="top">
      {/* Header */}
      <header className={styles.header}>
        <h1 className={styles.title}>Risk Disclosure</h1>
        <p className={styles.lastUpdated}>Last updated: March 15, 2026</p>
      </header>

      <div className={styles.dangerCallout}>
        <p>
          <strong>Please read this entire Risk Disclosure carefully before using SignalDeck.</strong> Trading securities
          involves substantial risk and may not be suitable for all investors. You could lose some or all of your
          invested capital.
        </p>
      </div>

      {/* Table of Contents */}
      <nav className={styles.toc}>
        <p className={styles.tocTitle}>Table of Contents</p>
        <ol className={styles.tocList}>
          {TOC.map((item) => (
            <li key={item.id}>
              <a href={`#${item.id}`} className={styles.tocLink}>
                {item.label}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      {/* 1. Trading Risk */}
      <section className={styles.section} id="trading-risk">
        <h2 className={styles.sectionTitle}>1. Trading Risk</h2>
        <p>
          Trading stocks, options, and other securities involves substantial risk of loss. The value of securities can
          fluctuate significantly due to market conditions, economic factors, company performance, geopolitical events,
          and other factors beyond anyone's control or ability to predict.
        </p>
        <p>
          Intraday and short-term trading strategies carry additional risks compared to long-term investing, including
          higher transaction costs, increased exposure to market volatility, and the potential for rapid, significant
          losses.
        </p>
        <div className={styles.dangerCallout}>
          <p>
            <strong>You should only trade with money you can afford to lose.</strong> Do not trade with funds needed for
            essential expenses, emergency savings, or retirement.
          </p>
        </div>
      </section>

      {/* 2. No Guarantees */}
      <section className={styles.section} id="no-guarantees">
        <h2 className={styles.sectionTitle}>2. No Guarantees</h2>
        <p>
          SignalDeck provides AI-powered predictions, trading signals, and backtested strategy results. None of these
          constitute a guarantee of future performance.
        </p>
        <ul>
          <li>
            <strong>Past performance is not indicative of future results.</strong> A strategy that performed well
            historically may lose money in current or future market conditions.
          </li>
          <li>
            <strong>AI predictions are probabilistic estimates, not certainties.</strong> Machine learning models are
            trained on historical data and may fail to account for unprecedented events, regime changes, or structural
            market shifts.
          </li>
          <li>
            <strong>Backtested results are hypothetical.</strong> They do not account for slippage, liquidity
            constraints, order fill rates, or the psychological impact of real trading. Actual results will differ from
            backtested results.
          </li>
          <li>
            <strong>No trading system is infallible.</strong> Even well-designed strategies will experience drawdowns,
            losing streaks, and periods of underperformance.
          </li>
        </ul>
      </section>

      {/* 3. Not Financial Advice */}
      <section className={styles.section} id="not-advice">
        <h2 className={styles.sectionTitle}>3. Not Financial Advice</h2>
        <div className={styles.warningCallout}>
          <p>
            <strong>SignalDeck is a software tool, not a financial advisor.</strong>
          </p>
        </div>
        <p>
          Nothing on SignalDeck constitutes investment advice, financial advice, trading advice, or any other sort of
          professional advice. The information, predictions, and signals provided by the platform are for informational
          and educational purposes only.
        </p>
        <p>
          SignalDeck is not a registered investment advisor, broker-dealer, or financial planner. We do not make
          personalized recommendations based on your financial situation, risk tolerance, or investment objectives.
        </p>
        <p>
          <strong>You should consult with a licensed financial advisor</strong> before making investment decisions,
          especially if you are new to trading or considering strategies involving leverage, options, or algorithmic
          execution.
        </p>
      </section>

      {/* 4. Automated Trading Risks */}
      <section className={styles.section} id="automated-risk">
        <h2 className={styles.sectionTitle}>4. Automated Trading Risks</h2>
        <p>
          SignalDeck offers automated trading bots that can execute trades on your behalf. While automation can remove
          emotion from trading and execute strategies consistently, it introduces additional risks:
        </p>
        <ul>
          <li>
            <strong>Rapid execution:</strong> Bots can open and close positions in seconds. In volatile markets, this
            may result in a series of losing trades before you can intervene.
          </li>
          <li>
            <strong>Software bugs:</strong> Despite thorough testing, software can contain bugs that cause unexpected
            behavior, including placing incorrect orders, failing to close positions, or executing trades at unintended
            prices.
          </li>
          <li>
            <strong>Connectivity issues:</strong> Internet outages, API downtime, or latency between SignalDeck and your
            brokerage can cause orders to fail, be delayed, or execute at different prices than expected.
          </li>
          <li>
            <strong>Market conditions beyond model parameters:</strong> Algorithms are designed for specific market
            conditions. Flash crashes, circuit breakers, halted trading, or extreme volatility events may cause
            strategies to behave unpredictably.
          </li>
          <li>
            <strong>Over-optimization:</strong> Strategies that are heavily optimized on historical data may perform
            poorly in live markets (overfitting).
          </li>
        </ul>
        <div className={styles.callout}>
          <p>
            <strong>Recommendation:</strong> Start with a paper trading account to familiarize yourself with how bots
            operate before deploying them with real capital. Monitor your bots regularly and set appropriate position
            size limits and stop-loss levels.
          </p>
        </div>
      </section>

      {/* 5. Options Risk */}
      <section className={styles.section} id="options-risk">
        <h2 className={styles.sectionTitle}>5. Options Risk</h2>
        <div className={styles.dangerCallout}>
          <p>
            <strong>
              Options are complex financial instruments with significant risk of loss. Options trading is not suitable
              for all investors.
            </strong>
          </p>
        </div>
        <p>Before trading options through SignalDeck, you should understand the following risks:</p>
        <ul>
          <li>Options can expire worthless, resulting in a total loss of the premium paid.</li>
          <li>
            Certain options strategies (such as writing uncovered calls) can expose you to theoretically unlimited
            losses.
          </li>
          <li>
            Options prices are affected by multiple factors including the underlying price, time decay (theta), implied
            volatility, and interest rates. These factors can interact in complex ways.
          </li>
          <li>Options may be illiquid, making it difficult to close positions at favorable prices.</li>
          <li>Early assignment risk exists for American-style options.</li>
        </ul>
        <p>
          You should read and understand the Characteristics and Risks of Standardized Options disclosure document from
          the Options Clearing Corporation (OCC) before trading options.
        </p>
      </section>

      {/* 6. Market Data */}
      <section className={styles.section} id="market-data">
        <h2 className={styles.sectionTitle}>6. Market Data</h2>
        <p>
          Market data, prices, charts, and other financial information displayed on SignalDeck is sourced from
          third-party providers (including Alpaca Markets) and may be:
        </p>
        <ul>
          <li>Delayed by varying amounts of time</li>
          <li>Subject to inaccuracies or errors in transmission</li>
          <li>Temporarily unavailable due to provider outages</li>
        </ul>
        <p>
          SignalDeck is not responsible for the accuracy, completeness, or timeliness of market data provided by third
          parties. You should not rely solely on data displayed in SignalDeck for time-sensitive trading decisions.
          Always verify critical information with your brokerage platform or an independent data source.
        </p>
      </section>

      {/* 7. Regulatory Compliance */}
      <section className={styles.section} id="regulatory">
        <h2 className={styles.sectionTitle}>7. Regulatory Compliance</h2>
        <p>
          You are solely responsible for ensuring that your trading activities comply with all applicable federal,
          state, local, and international securities laws and regulations. This includes but is not limited to:
        </p>
        <ul>
          <li>Pattern Day Trader (PDT) rules if applicable to your account</li>
          <li>Tax reporting requirements for securities transactions</li>
          <li>Restrictions on trading certain securities in your jurisdiction</li>
          <li>Insider trading and market manipulation prohibitions</li>
          <li>Any applicable FINRA, SEC, or other regulatory body requirements</li>
        </ul>
        <p>
          SignalDeck does not monitor your compliance with these regulations. If you are unsure about your regulatory
          obligations, consult a qualified legal or financial professional.
        </p>
      </section>

      {/* Back to top */}
      <div className={styles.backToTop}>
        <a href="#top" className={styles.backToTopLink}>
          Back to top
        </a>
      </div>
    </div>
  );
};

export default RiskDisclosure;
